import { Component, Input, OnInit, Output, EventEmitter } from "@angular/core"
import { FormGroup, FormBuilder } from "@angular/forms"
import sweetAlert from '../../../bmAlert'
import { ValidationService } from "services/validation-service"
import { externalRoute } from "modules/keywords/external-routes"
import { get } from "lodash-es"

@Component({
  selector: 'seo-requirements',
  template: `
  <div *ngIf="!hasSeoRequirements() || editingSeoRequirements"
    (click)="openSeoDialog()"
    [class.empty]="!hasSeoRequirements()"
    [class.full]="hasSeoRequirements()"
    [class.hover]="editingSeoRequirements"
    class="editable-field">
    <i class="editable-field-icon fas fa-link tiny-right-buffer"></i>
    <span class="text-info cursor-pointer">
      <span *ngIf="!hasSeoRequirements()" class="cursor-pointer">Add </span>SEO Requirements
    </span>
    <pop-card
      *ngIf="editingSeoRequirements"
      (outsideClick)="cancelSeoDialog()"
      class="small-bottom-buffer-lg">
      <form [formGroup]="seoRequirements">
        <div class="padding">
          <div (click)="cancelSeoDialog($event)" class="fas fa-times text-danger float-right cursor-pointer"></div>
          <div class="form-group">
            <label class="text-default">
              Focus Keyword(s)
              <i class="tiny-left-buffer fas fa-question-circle text-secondary"
                [popover]="focusKeywordsTooltip"
                container="body"
                placement="top"
                triggers="mouseenter:mouseleave"></i>
              <ng-template #focusKeywordsTooltip>
                <div class="font-weight-500 small" style="max-width: 300px;">
                  If targeting multiple keywords, list your primary keywords first, and separate
                  additional keywords with commas. Our writers will naturally incorporate your keyword(s),
                  and use the primary keyword in the topic title unless otherwise specified.
                </div>
              </ng-template>
            </label>
            <input formControlName="focusKeywords" class="form-control square-corners">
          </div>

          <div class="form-group">
            <label class="text-default">
              Internal Link
              <i class="tiny-left-buffer fas fa-question-circle text-secondary"
                [popover]="linkTooltip"
                container="body"
                placement="top"
                triggers="mouseenter:mouseleave"></i>
              <ng-template #linkTooltip>
                <div class="font-weight-500 small" style="max-width: 300px;">
                  Linking to another page on your site will guide readers
                  to additional useful content and increase engagement.
                  Use a descriptive word or phrase for anchor text to tell
                  readers where the link will take them.
                </div>
              </ng-template>
            </label>
            <input formControlName="link"
              name="link"
              class="form-control square-corners"
              [class.border-danger]="seoRequirements.controls.link.invalid"
              (input)="trimLink()"
              placeholder="URL"/>
            <input formControlName="linkText"
              [class.border-danger]="!seoRequirements.value.link && seoRequirements.value.linkText"
              class="form-control square-corners"
              placeholder="Anchor Text"/>
            <p class="text-danger small">
              <span *ngIf="seoRequirements.controls.link.invalid">invalid url</span>
              <span *ngIf="!seoRequirements.value.link && seoRequirements.value.linkText">
                needs url for anchor text
              </span>
            </p>
          </div>

          <div class="form-group">
            <label class="d-block text-default">
              Include external links?
              <i class="tiny-left-buffer fas fa-question-circle text-secondary"
                [popover]="externalLinksTooltip"
                container="body"
                placement="top"
                triggers="mouseenter:mouseleave"></i>
              <ng-template #externalLinksTooltip>
                <div class="font-weight-500 small" style="max-width: 300px;">
                  Linking to relevant, reputable sources not on your website
                  lends credibility to your content in the eyes of both search
                  engines and human readers.
                </div>
              </ng-template>
            </label>
            <label class="font-weight-normal small-right-buffer small" for="yes">
              <input type="radio" id="yes" name="externalLinks" formControlName="externalLinks" [value]="true"/>
              Yes (recommended)
            </label>
            <label class="font-weight-normal small" for="no">
              <input type="radio" id="no" name="externalLinks" formControlName="externalLinks" [value]="false"/>
              No
            </label>
          </div>

          <div class="form-group">
            <label class="text-default">
              Searcher Intent
              <i class="tiny-left-buffer fas fa-question-circle text-secondary"
                [popover]="searcherIntentTooltip"
                container="body"
                placement="top"
                triggers="mouseenter:mouseleave"></i>
              <ng-template #searcherIntentTooltip>
                <div class="font-weight-500 small" style="max-width: 300px;">
                  What are searchers trying to accomplish when they
                  perform the search you hope this content will rank for?
                  Research shows creating content that solves for
                  searcher intent can improve SEO.
                </div>
              </ng-template>
            </label>
            <textarea formControlName="searcherIntent" class="form-control square-corners" placeholder=""></textarea>
          </div>

          <div class="form-group">
            <label class="text-default">
              Other SEO Notes?
              <i class="tiny-left-buffer fas fa-question-circle text-secondary"
                [popover]="seoNotesTooltip"
                container="body"
                placement="top"
                triggers="mouseenter:mouseleave"></i>
              <ng-template #seoNotesTooltip>
                <div class="font-weight-500 small" style="max-width: 300px;">
                  Perhaps you'd also like to include:
                  <ul class="mt-2 left-padding">
                    <li>Preferred keyword usage</li>
                    <li>Acceptable sites to link to</li>
                    <li>Unnacceptable sites to link to</li>
                    <li>Context for your internal link</li>
                    <li>Date ranges for articles to link to</li>
                  </ul>
                </div>
              </ng-template>
            </label>
            <textarea formControlName="seoNotes" class="form-control square-corners" placeholder=""></textarea>
          </div>

          <button
            class="top-buffer blue-button btn-block"
            (click)="onSubmitSeoRequirements()"
            [disabled]="seoRequirements.invalid">Add SEO Requirements</button>
          <hr>

          <div class="d-flex justify-content-start">
            <div class="fas fa-question-circle text-secondary tiny-right-buffer"></div>
            <div class="small text-muted">
              Confused about SEO?
              <a href="{{linkTo('seo_requirements')}}" target="_blank">Learn more!</a>
            </div>
          </div>
        </div>
        <div
          class="cursor-pointer mt-2 small-padding bg-gray text-center text-danger"
          (click)="onRemoveSeoRequirements()">
          <span>Remove SEO Requirements</span>
        </div>
      </form>
    </pop-card>
  </div>
  <accordion-fade *ngIf="hasSeoRequirements()">
    <div class="editable-field"
      [ngClass]="{
        'empty': !hasSeoRequirements(),
        'full': hasSeoRequirements()
      }"
      (click)="$event.stopPropagation(); openSeoDialog()">
      <div class="editable-field-icon fas fa-link mr-2"></div>
      <div class="font-weight-500">
        <span>SEO Requirements</span>
      </div>
      <div (click)="$event.stopPropagation(); onRemoveSeoRequirements()"
        class="editable-field-remove ml-2"></div>
    </div>
    <ul>
      <li *ngIf="form.value.focusKeywords">
        Focus Keywords: {{form.value.focusKeywords}}
      </li>
      <li *ngIf="form.value.link" class="mt-2 text-truncate">
        Internal Link:
        <a [href]="form.value.link" target="_blank">{{form.value.linkText || form.value.link}}</a>
      </li>
      <li *ngIf="form.value.externalLinks == true || form.value.externalLinks == false" class="mt-2">
        External Links?: {{form.value.externalLinks ? 'Yes' : 'No'}}
      </li>
      <li *ngIf="form.value.searcherIntent" class="mt-2">
        Searcher Intent: {{form.value.searcherIntent}}
      </li>
      <li *ngIf="form.value.seoNotes">
        Other SEO Notes: {{form.value.seoNotes}}
      </li>
    </ul>
  </accordion-fade>
  `
})
export class SeoRequirementsComponent implements OnInit {

  @Input() form: FormGroup = null;
  @Output() seoRequirementsReady = new EventEmitter<FormGroup>();

  seoRequirements: FormGroup;
  editingSeoRequirements: boolean = false;

  constructor(
    private fb: FormBuilder
  ) { }

  ngOnInit(): void {
    this.resetSeoRequirements()
  }

  hasSeoRequirements(): boolean {
    const f = this.form.value
    return f.focusKeywords
      || f.link
      || f.linkText
      || f.externalLinks === true || f.externalLinks === false
      || f.searcherIntent
      || f.seoNotes
  }

  resetSeoRequirements(): void {
    this.seoRequirements = this.fb.group({
      focusKeywords: get(this.form.value, 'focusKeywords', null),
      link: [get(this.form.value, 'link', null), ValidationService.urlValidator] ,
      linkText: get(this.form.value, 'linkText', null),
      externalLinks: get(this.form.value, 'externalLinks', null),
      searcherIntent: get(this.form.value, 'searcherIntent', null),
      seoNotes: get(this.form.value, 'seoNotes', null),
    })
  }
  openSeoDialog(): void {
    this.editingSeoRequirements = true
  }
  cancelSeoDialog(event?: Event): void {
    if (event) event.stopPropagation()
    const changed = this.seoRequirements.dirty
    if (changed) {
      sweetAlert.confirm({
        text: 'Cancel changes made to SEO requirements?',
        confirmButtonText: "Close without saving",
        cancelButtonText: 'Continue making changes'
      }).then(confirm => {
        if (confirm) {
          this.resetSeoRequirements()
          this.editingSeoRequirements = false
        }
      })
    } else {
      this.editingSeoRequirements = false
    }
  }

  onSubmitSeoRequirements(): void {
    if (this.seoRequirements.valid && !(!this.seoRequirements.value.link && this.seoRequirements.value.linkText)) {
      this.form.setValue(Object.assign({}, this.form.value, this.seoRequirements.value))
      this.editingSeoRequirements = false
    }
  }

  onRemoveSeoRequirements(): void {
    const remove = () => {
      this.seoRequirements.setValue({
        focusKeywords: '',
        link: '',
        linkText: '',
        externalLinks: null,
        searcherIntent: '',
        seoNotes: '',
      })
      this.onSubmitSeoRequirements()
    }
    if (this.hasSeoRequirements()) {
      sweetAlert.confirm({text: "Remove all SEO Requirements?"}).then(confirm => { if (confirm) remove() })
    } else {
      remove()
    }
  }

  trimLink(): void {
    const link = this.seoRequirements.controls.link.value
    if (link) this.seoRequirements.controls.link.setValue(link.trim())
  }

  linkTo(externalRouteKey: string): string {
    return externalRoute(externalRouteKey)
  }
}
